import { useEffect, useState } from 'react';
import { parentApi } from './services/parentApi';

/**
 * Supervision status — "does this account supervise anybody?"
 *
 * The shell needs this on every page to decide whether the Parents nav item
 * belongs in the sidebar, but /family is the only screen that actually loads
 * the student list. Rather than refetch per navigation, this is a tiny
 * module-level store: one in-flight request per session, shared by every
 * consumer.
 *
 * No backend change was needed for this. GET /api/v1/parent/students is
 * auth-only — docs/parent-portal-subscription-policy.md lists it among the
 * endpoints that must return 200 for any authenticated user regardless of
 * subscription — so a learner-only account gets an empty list, not an error.
 *
 * The answer is also cached in localStorage per uid, so a returning
 * supervisor's nav renders correctly on the first paint instead of having
 * the Parents item pop in a few hundred ms later. That cache is only ever a
 * HINT: it is revalidated on every session, and being briefly wrong costs
 * nothing (the /family route stays reachable either way).
 */

const STORAGE_PREFIX = 'graspd.supervises.';

let state = { supervises: false, ready: false };
let currentUid = null;
const listeners = new Set();

function emit() {
  listeners.forEach((fn) => fn(state));
}

function setState(next) {
  state = { ...state, ...next };
  emit();
}

function readCache(uid) {
  if (!uid) return null;
  try {
    const raw = localStorage.getItem(STORAGE_PREFIX + uid);
    return raw === null ? null : raw === 'true';
  } catch {
    return null;
  }
}

function writeCache(uid, supervises) {
  if (!uid) return;
  try {
    localStorage.setItem(STORAGE_PREFIX + uid, String(supervises));
  } catch {
    /* private mode / quota — the cache is only a paint hint, so losing it is fine */
  }
}

/**
 * Record the answer we already have. /family loads the full student list
 * anyway, so it reports the count here rather than making this module fetch
 * a second copy — and link/unlink flow through the same path, which is what
 * makes the nav item appear the moment a first student is linked.
 */
export function reportSupervisedCount(count) {
  const supervises = count > 0;
  writeCache(currentUid, supervises);
  if (state.supervises !== supervises || !state.ready) {
    setState({ supervises, ready: true });
  }
}

/** Called by the shell when the signed-in user changes (including sign-out). */
export function primeSupervisionStatus(uid) {
  // Also the request-dedup guard: repeat calls for the same signed-in user
  // (every route change re-runs the effect) return before fetching.
  if (uid === currentUid) return;
  currentUid = uid;

  if (!uid) {
    setState({ supervises: false, ready: false });
    return;
  }

  const cached = readCache(uid);
  // `ready` stays false on a cache hit: the value is good enough to paint
  // with, but it has not been confirmed this session yet.
  setState({ supervises: cached === true, ready: false });

  parentApi
    .listStudents({})
    .then((data) => {
      const count = Array.isArray(data?.students) ? data.students.length : 0;
      if (uid !== currentUid) return; // user switched mid-flight
      reportSupervisedCount(count);
    })
    .catch(() => {
      if (uid !== currentUid) return;
      // A failed lookup must not hide a supervisor's only way back to their
      // students, so fall back to the cached hint rather than to `false`.
      setState({ supervises: readCache(uid) === true, ready: true });
    });
}

export function useSupervisionStatus() {
  const [snapshot, setSnapshot] = useState(state);

  useEffect(() => {
    listeners.add(setSnapshot);
    setSnapshot(state);
    return () => listeners.delete(setSnapshot);
  }, []);

  return snapshot;
}

/** Test seam — resets the module store between cases. */
export function __resetSupervisionStatus() {
  state = { supervises: false, ready: false };
  currentUid = null;
  listeners.clear();
}
